import { ref } from "vue";
import api from "../api/axios";

const user = ref(null);
const loading = ref(true);
// true una vez que /me respondió al menos una vez (con sesión o sin ella).
let sesionResuelta = false;
// Promesa en curso, si la hay: App.vue y el guard global del router llaman
// a loadUser() casi al mismo tiempo al arrancar, ambos antes de que
// sesionResuelta se ponga en true — sin este cache disparan dos /me (y
// potencialmente dos /refresh en cascada) por la misma carga de página.
let peticionEnCurso = null;

// 🔄 Cargar usuario
// (reintenta si el usuario en memoria está incompleto, ej. sin "name" tras
// el login; pero NO si ya se confirmó que no hay sesión — el guard global
// del router llama a esto en cada navegación, y sin este corte cada
// navegación sin sesión dispara /me y, en cascada, un intento de /refresh
// que comparte rate limit con /login).
// El accessToken vive en cookie httpOnly (JS no puede leerlo), así que la
// única forma de saber si hay sesión es preguntarle al backend.
const loadUser = async () => {
  if (sesionResuelta && (user.value === null || user.value?.name)) return;
  if (peticionEnCurso) return peticionEnCurso;

  peticionEnCurso = (async () => {
    try {
      const res = await api.get("/me");
      user.value = res.data.user;
    } catch (err) {
      console.log("❌ Sin sesión activa");

      user.value = null;

      // La redirección a /signin la hace el guard global del router
      // (router/index.js), que ya llama a loadUser() y evalúa user.value
      // después — no hace falta (ni conviene, para no pisar la navegación
      // en curso) redirigir también desde acá.
    } finally {
      sesionResuelta = true;
      loading.value = false;
      peticionEnCurso = null;
    }
  })();

  return peticionEnCurso;
};

// 👀 Vigilar la sesión
// Sin esto, una pestaña abierta sin actividad sigue mostrándose "logueada"
// aunque la sesión se haya revocado: el front solo se entera cuando hace
// alguna petición. Se consulta /me al volver a la pestaña y cada minuto
// mientras está visible; si la sesión murió, el interceptor de axios
// (api/axios.js) manda al login.
const INTERVALO_VERIFICACION_MS = 60 * 1000;
let vigilando = false;

const verificarSesion = () => {
  if (!user.value || document.visibilityState !== "visible") return;
  api.get("/me").catch(() => { /* el interceptor ya redirige si hace falta */ });
};

const vigilarSesion = () => {
  if (vigilando) return;
  vigilando = true;

  document.addEventListener("visibilitychange", verificarSesion);
  window.addEventListener("focus", verificarSesion);
  setInterval(verificarSesion, INTERVALO_VERIFICACION_MS);
};

// 🔐 LOGIN
const login = async (email, password) => {
  try {
    const res = await api.post("/login", { email, password });

    // accessToken/refreshToken ya no vienen en el body: el backend los
    // setea como cookies httpOnly en la misma respuesta.
    const { user: userData } = res.data;

    user.value = userData;

    return userData;
  } catch (err) {
    console.error("LOGIN ERROR:", err.response?.data);
    throw err;
  }
};

// 🚪 LOGOUT
const logout = async () => {
  try {
    // El refreshToken va en cookie; el backend lo lee de ahí y limpia
    // tanto la fila en BD como las cookies de sesión.
    await api.post("/logout");
  } catch (err) {
    console.error("Logout error:", err);
  }

  user.value = null;
};

// 🛡️ Permisos
const hasPermission = (permission) => {
  if (!user.value) return false;
  return user.value.permissions?.includes(permission);
};

// 🛡️ Roles
const hasRole = (role) => {
  if (!user.value) return false;
  return user.value.role === role;
};

export function useAuth() {
  return {
    user,
    loading,
    loadUser,
    login,
    logout,
    vigilarSesion,
    hasPermission,
    hasRole,
  };
}